import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { sewerPickupService, Sewer, SewerAssignmentHistory } from '../../api/services/sewerPickupService';

const SewerPickup: React.FC = () => {
  const [sewers, setSewers] = useState<Sewer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedSewer, setSelectedSewer] = useState<Sewer | null>(null);
  const [orderItemNumber, setOrderItemNumber] = useState<string>('');
  const [isScanModalOpen, setIsScanModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [assignmentHistory, setAssignmentHistory] = useState<SewerAssignmentHistory[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [errorMsgSpanish, setErrorMsgSpanish] = useState<string>('');
  const scanInputRef = useRef<HTMLInputElement>(null);
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadSewers();

    // Auto-reload page every 10 minutes
    const reloadTimeout = setTimeout(() => {
      window.location.reload();
    }, 10 * 60 * 1000);

    return () => clearTimeout(reloadTimeout);
  }, []);

  useEffect(() => {
    if (isScanModalOpen && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [isScanModalOpen]);

  // Watch for scan input
  useEffect(() => {
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    // Check if input matches the pattern: orderId-ordinal-itemNumber
    // eslint-disable-next-line no-useless-escape
    const scanPattern = /\d+[-\[]\d+[-\[]\d+/;
    if (scanPattern.test(orderItemNumber)) {
      // Replace [ with - if present
      const normalized = orderItemNumber.replace(/\[/g, '-');
      setOrderItemNumber(normalized);

      // Auto-submit after 1 second
      scanTimeoutRef.current = setTimeout(() => {
        assignSewer();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemNumber]);

  const loadSewers = async () => {
    try {
      const data = await sewerPickupService.getSewers();
      setSewers(data.sort((a, b) => a.FirstName.localeCompare(b.FirstName)));
    } catch (error) {
      console.error('Error loading sewers:', error);
    } finally {
      setLoading(false);
    }
  };

  const showScanModal = (sewer: Sewer) => {
    setSelectedSewer(sewer);
    setOrderItemNumber('');
    setIsScanModalOpen(true);
  };

  const closeScanModal = () => {
    setIsScanModalOpen(false);
    setSelectedSewer(null);
    setOrderItemNumber('');
  };

  const assignSewer = async () => {
    if (!selectedSewer || !orderItemNumber) return;

    try {
      const result = await sewerPickupService.assignSewer({
        sewerId: selectedSewer.Id,
        orderItemNumber
      });

      closeScanModal();

      if (result.success) {
        setAssignmentHistory(result.data as SewerAssignmentHistory[]);
        setIsSuccessModalOpen(true);
      } else {
        const errorData = result.data as { actionId: number; sewer?: string } | undefined;

        if (errorData?.actionId === 99) {
          setErrorMsg('Item has been canceled. Please choose another item.');
          setErrorMsgSpanish('Pedido a sido cancelado porfavor escojer otro pedido.');
        } else if (errorData?.sewer) {
          setErrorMsg(`Item is currently assigned to ${errorData.sewer}.`);
          setErrorMsgSpanish(`Este pedido ya está asignado a ${errorData.sewer}.`);
        } else {
          setErrorMsg(result.message || 'An error occurred');
          setErrorMsgSpanish('');
        }

        setIsErrorModalOpen(true);
      }
    } catch (error) {
      console.error('Error assigning sewer:', error);
      setErrorMsg('An error occurred while assigning the item.');
      setErrorMsgSpanish('');
      setIsErrorModalOpen(true);
    }
  };

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Sewer Pickup
            </h2>
            <p className="mt-2 text-lg text-gray-700 dark:text-gray-300">
              Please choose your name (Por favor, escoja su nombre)
            </p>
          </div>

          {/* Sewers Grid */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sewers.map((sewer) => (
                <button
                  key={sewer.Id}
                  onClick={() => showScanModal(sewer)}
                  className="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900 border border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center transition-colors h-24 flex items-center justify-center"
                >
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {sewer.FirstName} {sewer.LastName} ({sewer.Tag})
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scan Modal */}
      {isScanModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeScanModal}
          ></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Scan your item
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                Please scan your item<br />
                Por favor, escanear la copia impresa
              </p>
              <input
                ref={scanInputRef}
                type="text"
                value={orderItemNumber}
                onChange={(e) => setOrderItemNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Scan barcode..."
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={closeScanModal}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsSuccessModalOpen(false)}
          ></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Item accepted
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-600 text-green-700 dark:text-green-100 px-4 py-3 rounded mb-4">
                <p className="text-lg font-semibold">
                  Item has been accepted.<br />
                  Pedido ha sido aceptado.
                </p>
              </div>
              {assignmentHistory.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Assignment History
                  </h4>
                  <table className="min-w-full border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                          Part Number
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                          Assignment Date
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600">
                          Sewer
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignmentHistory.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                            {item.PartNumber}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                            {format(new Date(item.CreateDate), 'M/d/yy h:mm a')}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                            {item.FirstName} {item.LastName}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setIsSuccessModalOpen(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsErrorModalOpen(false)}
          ></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Item not accepted / Pedido no aceptado
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-100 px-4 py-3 rounded">
                <p className="text-lg font-semibold">
                  {errorMsg}<br />
                  {errorMsgSpanish && errorMsgSpanish}
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setIsErrorModalOpen(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default SewerPickup;
